import React, { useState, useEffect } from 'react'
import { useTenant } from '../../contexts/TenantContext'

const CMSDashboard = () => {
  const { tenant } = useTenant()
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    archived: 0
  })

  useEffect(() => {
    fetchPages()
  }, [])

  const fetchPages = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('tenant_admin_token')
      
      const response = await fetch('/api/client-admin/pages', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'X-Original-Host': window.location.hostname
        }
      })

      if (response.ok) {
        const data = await response.json()
        const pageList = data.data?.pages || []
        setPages(pageList)
        
        // Calculate stats
        setStats({
          total: pageList.length,
          published: pageList.filter(p => p.status === 'published').length,
          draft: pageList.filter(p => p.status === 'draft').length,
          archived: pageList.filter(p => p.status === 'archived').length
        })
      }
    } catch (error) {
      console.error('Failed to fetch pages:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      published: { class: 'status-published', text: 'Published' },
      draft: { class: 'status-draft', text: 'Draft' },
      archived: { class: 'status-archived', text: 'Archived' }
    }
    return badges[status] || badges.draft
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const createPageFromTemplate = (template) => {
    // Redirect to page editor with template
    window.location.href = `/admin/pages/new?template=${template}`
  }

  return (
    <div className="cms-dashboard">
      <div className="cms-header">
        <div className="header-content">
          <h1>Content Management</h1>
          <p>Manage your organization's website content and pages</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn-primary"
            onClick={() => window.location.href = '/admin/pages/new'}
          >
            <i className="fas fa-plus"></i>
            Create New Page
          </button>
        </div>
      </div>

      <div className="cms-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-file-alt"></i>
          </div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Pages</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-eye"></i>
          </div>
          <div className="stat-value">{stats.published}</div>
          <div className="stat-label">Published</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-edit"></i>
          </div>
          <div className="stat-value">{stats.draft}</div>
          <div className="stat-label">Drafts</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-archive"></i>
          </div>
          <div className="stat-value">{stats.archived}</div>
          <div className="stat-label">Archived</div>
        </div>
      </div>

      <div className="cms-content">
        <div className="content-header">
          <h2>Your Pages</h2>
          <div className="content-filters">
            <select defaultValue="">
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
            <input type="text" placeholder="Search pages..." />
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            Loading pages...
          </div>
        ) : pages.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-file-plus"></i>
            <h3>No Pages Yet</h3>
            <p>Get started by creating your first page for {tenant?.name}</p>
            <button 
              className="btn-primary"
              onClick={() => window.location.href = '/admin/pages/new'}
            >
              Create Your First Page
            </button>
          </div>
        ) : (
          <div className="pages-table">
            <div className="table-header">
              <div>Title</div>
              <div>Template</div>
              <div>Status</div>
              <div>Last Updated</div>
              <div>Actions</div>
            </div>
            {pages.map(page => (
              <div key={page.id} className="table-row">
                <div className="page-title">
                  <strong>{page.title}</strong>
                  <small>/{page.slug}</small>
                </div>
                <div className="page-template">{page.template || 'default'}</div>
                <div className="page-status">
                  <span className={`status-badge ${getStatusBadge(page.status).class}`}>
                    {getStatusBadge(page.status).text}
                  </span>
                </div>
                <div className="page-date">{formatDate(page.updated_at)}</div>
                <div className="page-actions">
                  <button className="btn-edit">
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="btn-view">
                    <i className="fas fa-eye"></i>
                  </button>
                  <button className="btn-delete">
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="quick-templates">
        <h2>Quick Start Templates</h2>
        <div className="templates-grid">
          <div className="template-card" onClick={() => createPageFromTemplate('homepage')}>
            <i className="fas fa-home"></i>
            <h4>Homepage</h4>
            <p>Main landing page for your organization</p>
          </div>
          <div className="template-card" onClick={() => createPageFromTemplate('about')}>
            <i className="fas fa-info-circle"></i>
            <h4>About Us</h4>
            <p>Tell your organization's story</p>
          </div>
          <div className="template-card" onClick={() => createPageFromTemplate('contact')}>
            <i className="fas fa-envelope"></i>
            <h4>Contact</h4>
            <p>Contact information and location</p>
          </div>
          <div className="template-card" onClick={() => createPageFromTemplate('history')}>
            <i className="fas fa-history"></i>
            <h4>History</h4>
            <p>Your organization's heritage</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CMSDashboard